import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { appeler } from '../../api/client.ts';
import { Chargement, DateHeure, Erreur } from '../../composants/communs.tsx';
import { ImageProtegee } from '../../composants/ImageProtegee.tsx';
import { useSession } from '../../composants/session.tsx';

interface PhotoPanier {
  id: string;
  ajoute_le: string;
}

/**
 * Photo(s) du panier — RG-168, RG-169.
 *
 * Masqué en silence sans la permission `vente.photo.lire` : l'écran de
 * détail reste utilisable à qui n'a que `vente.lire`, sans un bloc qui ne
 * ferait qu'échouer en boucle.
 */
export function BlocPhotos({ venteId }: { venteId: string }): ReactNode {
  const { peut } = useSession();
  const autorise = peut('vente.photo.lire');
  const [agrandie, setAgrandie] = useState<PhotoPanier | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['vente-photos', venteId],
    queryFn: () => appeler<{ photos: PhotoPanier[] }>(`/ventes/${venteId}/photos`),
    enabled: autorise,
  });

  if (!autorise) return null;
  if (isPending) return <Chargement />;
  if (error !== null) return <Erreur erreur={error} />;

  return (
    <section aria-label="Photo du panier" className="vente-section">
      <div className="vente-section__tete">
        <h2>Photo du panier</h2>
      </div>

      {data.photos.length === 0 ? (
        <p className="table-vide">
          Aucune photo pour cette vente. Si la boutique l’exige, l’appareil
          l’envoie APRÈS que la vente a été remontée — le délai est normal
          tant qu’elle reste « en attente ».
        </p>
      ) : (
        <div className="pile" style={{ gap: 'var(--espace-3)' }}>
          {data.photos.map((photo) => (
            <div key={photo.id}>
              <button
                type="button"
                className="fiche-photo"
                aria-label="Agrandir la photo du panier"
                onClick={() => {
                  setAgrandie(photo);
                }}
              >
                <span className="fiche-photo__cadre">
                  <ImageProtegee chemin={`/photos/${photo.id}`} alt="Photo du panier à la caisse" />
                </span>
                <span className="fiche-photo__agrandir">Agrandir</span>
              </button>
              <p className="entete__sous" style={{ marginTop: 6 }}>
                <DateHeure valeur={photo.ajoute_le} />
              </p>
            </div>
          ))}
        </div>
      )}

      {agrandie !== null && (
        <Visionneuse
          onFermer={() => {
            setAgrandie(null);
          }}
        >
          <div className="visionneuse__tete">
            <span style={{ fontWeight: 600 }}>
              Photo du panier · <DateHeure valeur={agrandie.ajoute_le} />
            </span>
            <button
              type="button"
              className="visionneuse__fermer"
              aria-label="Fermer"
              onClick={() => {
                setAgrandie(null);
              }}
            >
              ×
            </button>
          </div>
          <ImageProtegee chemin={`/photos/${agrandie.id}`} alt="Photo du panier à la caisse, en plein écran" />
        </Visionneuse>
      )}
    </section>
  );
}

/** Visionneuse plein écran, fond sombre — distincte de `Modale` (toujours claire). */
function Visionneuse({ onFermer, children }: { onFermer: () => void; children: ReactNode }): ReactNode {
  return (
    <div className="visionneuse-fond" role="presentation" onClick={onFermer}>
      <div
        className="visionneuse"
        role="dialog"
        aria-modal="true"
        onClick={(evenement) => {
          evenement.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>
  );
}
